import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import { formatPrice, toNumber } from "../../lib/formatters.ts";
import { CloseAllHeader } from "./close-all-header.tsx";
import { numericString } from "./lib/sorting-fns.ts";
import { OpenOrderCancelCell } from "./open-order-cancel-cell.tsx";
import { OpenOrderEditCell } from "./open-order-edit-cell.tsx";
import type {
	OpenOrderRowItem,
	OpenOrdersTableMeta,
} from "./types/positions.types.ts";

const col = createColumnHelper<OpenOrderRowItem>();

export function getOpenOrdersColumns() {
	return [
		col.accessor("coin", {
			header: () => <T keyName="orders.columns.market" />,
			cell: ({ getValue }) => (
				<span className="whitespace-nowrap text-label2-medium text-trade-text-strong">
					{getValue()}-PERP
				</span>
			),
		}),
		col.accessor("side", {
			header: () => <T keyName="orders.columns.side" />,
			cell: ({ getValue }) => {
				const isBuy = getValue() === "B";
				return (
					<span className={isBuy ? "text-trade-green" : "text-trade-red"}>
						{isBuy ? (
							<T keyName="orders.side.buy" />
						) : (
							<T keyName="orders.side.sell" />
						)}
					</span>
				);
			},
		}),
		col.accessor("orderType", {
			header: () => <T keyName="orders.columns.type" />,
			cell: ({ getValue }) => (
				<span className="text-trade-text-secondary">{getValue()}</span>
			),
		}),
		col.accessor("limitPx", {
			header: () => <T keyName="orders.columns.price" />,
			sortingFn: numericString,
			cell: ({ getValue }) => (
				<span className="text-label2-tabular-medium text-trade-text-secondary">
					{formatPrice(getValue())}
				</span>
			),
		}),
		col.accessor("sz", {
			header: () => <T keyName="orders.columns.size" />,
			sortingFn: numericString,
			cell: ({ getValue, row }) => (
				<span className="whitespace-nowrap text-label2-tabular-medium text-trade-text-secondary">
					{getValue()} {row.original.coin}
				</span>
			),
		}),
		col.accessor("triggerPx", {
			header: () => <T keyName="orders.columns.trigger" />,
			sortingFn: numericString,
			cell: ({ getValue }) => (
				<span className="text-label2-tabular-medium text-trade-text-secondary">
					{toNumber(getValue()) > 0 ? formatPrice(getValue()) : "N/A"}
				</span>
			),
		}),
		col.accessor("reduceOnly", {
			header: () => <T keyName="orders.columns.reduce-only" />,
			cell: ({ getValue }) => (
				<span className="text-trade-text-secondary">
					{getValue() ? (
						<T keyName="general.yes" />
					) : (
						<T keyName="general.no" />
					)}
				</span>
			),
		}),
		col.display({
			id: "actions",
			enableSorting: false,
			header: ({ table }) => {
				const meta = table.options.meta as OpenOrdersTableMeta;
				return <CloseAllHeader onConfirm={meta.onCancelAll} variant="cancel" />;
			},
			cell: ({ row, table }) => {
				const meta = table.options.meta as OpenOrdersTableMeta;
				const order = row.original;

				return (
					<div className="flex items-center gap-2">
						{/* Position TP/SL rows have their own editor (position-tpsl-cell). */}
						{!order.isPositionTpsl && (
							<OpenOrderEditCell order={order} onModify={meta.onModify} />
						)}
						<OpenOrderCancelCell order={order} onCancel={meta.onCancel} />
					</div>
				);
			},
		}),
	];
}
